import httpService from '../httpService';
import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import {
  CreateExpenseDto,
  Expense,
  ExpenseFilters,
  ExpenseSummary,
  PayExpenseDto,
} from '../../types/expense';
import { ReportResult } from '../../types/reports';
import { reportsService, ReportExportResult } from './reportsService';

/** Filters for the grouped "Expenses by Category" report. */
export interface ExpenseReportFilters {
  groupBy?: string;
  dateFrom?: string;
  dateTo?: string;
}

function buildReportQuery(filters?: ExpenseReportFilters): string {
  const params = new URLSearchParams();
  if (filters?.groupBy) params.set('groupBy', filters.groupBy);
  if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.set('dateTo', filters.dateTo);
  return params.toString();
}

function buildQuery(filters?: ExpenseFilters): string {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.vendorId) params.append('vendorId', filters.vendorId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.expenseType) params.append('expenseType', filters.expenseType);
  if (filters?.frequency) params.append('frequency', filters.frequency);
  if (filters?.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  params.append('page', String(filters?.page || 1));
  params.append('limit', String(filters?.limit || 20));
  return params.toString();
}

function buildSummaryQuery(filters?: ExpenseFilters): string {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.vendorId) params.append('vendorId', filters.vendorId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.expenseType) params.append('expenseType', filters.expenseType);
  if (filters?.frequency) params.append('frequency', filters.frequency);
  if (filters?.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const expenseService = {
  // ── Expenses ──────────────────────────────────────────────────────────
  getExpenses: (filters?: ExpenseFilters): Promise<ApiResponse<Expense[]> & { meta: PaginationMeta }> => {
    return get<Expense[]>(`/expenses?${buildQuery(filters)}`) as Promise<
      ApiResponse<Expense[]> & { meta: PaginationMeta }
    >;
  },

  getSummary: (filters?: ExpenseFilters): Promise<ApiResponse<ExpenseSummary>> => {
    return get<ExpenseSummary>(`/expenses/reports/summary${buildSummaryQuery(filters)}`);
  },

  /** Category-wise (or vendor / payment-method) expense totals for a period. */
  getCategoryReport: (filters?: ExpenseReportFilters): Promise<ApiResponse<ReportResult>> => {
    const qs = buildReportQuery(filters);
    return get<ReportResult>(`/expenses/reports/by-category${qs ? `?${qs}` : ''}`);
  },

  /** Download the grouped expense report (pdf | excel | csv). */
  exportCategoryReport: async (
    format: string,
    filters?: ExpenseReportFilters,
  ): Promise<ReportExportResult> => {
    const qs = buildReportQuery(filters);
    const response = await httpService.get(
      `/expenses/reports/by-category/export?format=${format}${qs ? `&${qs}` : ''}`,
      { responseType: 'blob' },
    );
    const blob = response.data as Blob;
    const filename = reportsService.filenameFromHeaders(
      response.headers?.['content-disposition'],
      'expenses-by-category',
      format,
    );
    return { blob, filename };
  },

  getExpense: (id: string): Promise<ApiResponse<Expense>> => {
    return get<Expense>(`/expenses/${id}`);
  },

  createExpense: (data: CreateExpenseDto): Promise<ApiResponse<Expense>> => {
    return post<Expense>('/expenses', data);
  },

  updateExpense: (id: string, data: Partial<CreateExpenseDto>): Promise<ApiResponse<Expense>> => {
    return patch<Expense>(`/expenses/${id}`, data);
  },

  payExpense: (id: string, data: PayExpenseDto): Promise<ApiResponse<Expense>> => {
    return post<Expense>(`/expenses/${id}/pay`, data);
  },

  deleteExpense: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/expenses/${id}`);
  },

  bulkDeleteExpenses: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/expenses/bulk-delete', { ids });
  },

  /** Fetch the printable expense voucher as an object URL (authenticated blob). */
  getVoucherUrl: async (id: string): Promise<string> => {
    const response = await httpService.get(`/expenses/${id}/voucher`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },
};
